import type {
  CatalogListOptions,
  CatalogPage,
  CatalogRepository,
} from '#/domain/catalog/CatalogRepository'
import type { CatalogMetric } from '#/domain/catalog/CatalogMetric'
import type { CatalogLayer } from '#/domain/catalog/CatalogLayer'
import type { CatalogApplication } from '#/domain/catalog/CatalogApplication'
import type { CatalogLayerCategory } from '#/domain/catalog/CatalogLayerCategory'
import type { LayerAssignmentBatchResult } from '#/domain/catalog/LayerAssignment'
import type { MetricCategory } from '#/domain/catalog/MetricCategory'
import type {
  BulkAssignMetricsToOrganizationPayload,
  OrganizationMetricAssignment,
} from '#/domain/catalog/OrganizationMetricAssignment'
import type {
  ApplyFireriskMetricPresetOptions,
  FireriskMetricPresetResult,
} from '#/domain/catalog/FireriskMetricPreset'
import type { HttpClientPort } from '#/infrastructure/persistence/HttpClientPort'
import { toDomainPage as toMetricPage, type CatalogMetricPageResponseDto } from './CatalogMetricDto'
import { toDomainPage as toLayerPage, type CatalogLayerPageResponseDto } from './CatalogLayerDto'
import {
  toDomainList as toApplicationList,
  type CatalogApplicationResponseDto,
} from './CatalogApplicationDto'
import { mapLayerCategoryDto, type LayerCategoryDtoResponse } from './LayerCategoryDto'
import {
  toDomainBatch as toLayerAssignmentBatch,
  type LayerAssignmentBatchResultResponseDto,
} from './LayerAssignmentDto'
import {
  toDomainList as toMetricCategoryList,
  type MetricCategoryResponseDto,
} from './MetricCategoryDto'
import {
  toDomainList as toOrganizationMetricAssignmentList,
  type OrganizationMetricAssignmentResponseDto,
} from './OrganizationMetricAssignmentDto'
import {
  toDomain as toFireriskMetricPresetResult,
  type FireriskMetricPresetResultResponseDto,
} from './FireriskMetricPresetDto'

export class HttpCatalogRepository implements CatalogRepository {
  constructor(private readonly http: HttpClientPort) {}

  async findMetrics(
    options: CatalogListOptions,
    search?: string,
    applicationId?: string,
    datasource?: string,
  ): Promise<CatalogPage<CatalogMetric>> {
    const raw = await this.http.get<CatalogMetricPageResponseDto>('v1/catalog/metrics', {
      searchParams: buildSearchParams(options, search, applicationId, datasource),
    })
    return toMetricPage(raw)
  }

  async findLayers(
    options: CatalogListOptions,
    search?: string,
    applicationId?: string,
  ): Promise<CatalogPage<CatalogLayer>> {
    const raw = await this.http.get<CatalogLayerPageResponseDto>('v1/catalog/layers', {
      searchParams: buildSearchParams(options, search, applicationId),
    })
    return toLayerPage(raw)
  }

  async findApplications(): Promise<CatalogApplication[]> {
    const raw = await this.http.get<CatalogApplicationResponseDto[]>('v1/catalog/applications')
    return toApplicationList(raw)
  }

  async findMetricsByOrganizationId(
    organizationId: string,
    options: CatalogListOptions,
    applicationId?: string,
    search?: string,
  ): Promise<CatalogPage<CatalogMetric>> {
    const raw = await this.http.get<CatalogMetricPageResponseDto>('v1/metric/assignments', {
      searchParams: buildOrganizationSearchParams(organizationId, options, applicationId, search),
    })
    return toMetricPage(raw)
  }

  async findLayersByOrganizationId(
    organizationId: string,
    options: CatalogListOptions,
  ): Promise<CatalogPage<CatalogLayer>> {
    const raw = await this.http.get<CatalogLayerPageResponseDto>('v1/layer/assignments', {
      searchParams: buildOrganizationSearchParams(organizationId, options),
    })
    return toLayerPage(raw)
  }

  async findLayerCategoriesByOrganizationId(
    organizationId: string,
  ): Promise<CatalogLayerCategory[]> {
    const raw = await this.http.get<LayerCategoryDtoResponse[]>('v1/layer/categories', {
      searchParams: new URLSearchParams({ organizationId }),
    })
    return raw.map(mapLayerCategoryDto)
  }

  async assignLayerToOrganization(
    organizationId: string,
    layerIds: string[],
    layerCategoryId?: string,
  ): Promise<LayerAssignmentBatchResult> {
    const raw = await this.http.post<LayerAssignmentBatchResultResponseDto>(
      `v1/clients/${organizationId}/layers`,
      {
        // PascalCase: the API binds request bodies with PascalCaseJsonNamingPolicy.
        json: { LayerIds: layerIds, LayerCategoryId: layerCategoryId },
      },
    )
    return toLayerAssignmentBatch(raw)
  }

  async unassignLayerFromOrganization(
    organizationId: string,
    layerId: string,
    layerCategoryId?: string,
  ): Promise<void> {
    const searchParams: Record<string, string> = {}
    if (layerCategoryId) searchParams.layerCategoryId = layerCategoryId

    await this.http.delete<void>(`v1/clients/${organizationId}/layers/${layerId}`, { searchParams })
  }

  async findMetricCategories(): Promise<MetricCategory[]> {
    const raw = await this.http.get<MetricCategoryResponseDto[]>('v1/metriccategory')
    return toMetricCategoryList(raw)
  }

  async findAssignedMetricIds(
    organizationId: string,
    applicationId: string,
    riskType?: string,
  ): Promise<string[]> {
    const params: Record<string, string> = { applicationId }
    if (riskType) params.riskType = riskType

    return await this.http.get<string[]>(
      `v1/metric/organizations/${organizationId}/assigned-metric-ids`,
      { searchParams: new URLSearchParams(params) },
    )
  }

  async bulkAssignMetricsToOrganization(
    organizationId: string,
    payload: BulkAssignMetricsToOrganizationPayload,
  ): Promise<OrganizationMetricAssignment[]> {
    const raw = await this.http.post<OrganizationMetricAssignmentResponseDto[]>(
      `v1/metric/organizations/${organizationId}/bulk`,
      {
        json: {
          applicationId: payload.applicationId,
          riskType: payload.riskType,
          minLevel: payload.minLevel,
          maxLevel: payload.maxLevel,
          metrics: payload.metrics.map((m) => ({
            metricId: m.metricId,
            displayOrder: m.displayOrder,
          })),
        },
      },
    )
    return toOrganizationMetricAssignmentList(raw)
  }

  async applyFireriskMetricPreset(
    organizationId: string,
    options: ApplyFireriskMetricPresetOptions,
  ): Promise<FireriskMetricPresetResult> {
    const raw = await this.http.post<FireriskMetricPresetResultResponseDto>(
      `v1/metric/organizations/${organizationId}/firerisk-preset`,
      {
        json: {
          includePof: options.includePof,
          includePoi: options.includePoi,
          includeOutages: options.includeOutages,
          dryRun: options.dryRun,
        },
      },
    )
    return toFireriskMetricPresetResult(raw)
  }

  async unassignMetricFromOrganization(
    organizationId: string,
    metricId: string,
    applicationId: string,
    riskType?: string | null,
  ): Promise<void> {
    const params: Record<string, string> = { applicationId }
    if (riskType) params.riskType = riskType

    await this.http.delete<void>(`v1/metric/${metricId}/organizations/${organizationId}`, {
      searchParams: new URLSearchParams(params),
    })
  }
}

function buildOrganizationSearchParams(
  organizationId: string,
  options: CatalogListOptions,
  applicationId?: string,
  search?: string,
): URLSearchParams {
  const params = buildSearchParams(options, search, applicationId)
  params.set('organizationId', organizationId)
  return params
}

function buildSearchParams(
  options: CatalogListOptions,
  search?: string,
  applicationId?: string,
  datasource?: string,
): URLSearchParams {
  const params: Record<string, string> = {
    page: String(options.page),
    itemsPerPage: String(options.itemsPerPage),
  }
  if (search) params.search = search
  if (applicationId) params.applicationId = applicationId
  if (datasource) params.datasource = datasource
  if (options.sortBy) params.sortBy = options.sortBy
  if (options.sortDescending) params.sortDescending = String(options.sortDescending)

  return new URLSearchParams(params)
}
